import { useCallback, useEffect, useState } from 'react';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { Badge, Layout, Menu, Modal, Switch } from 'antd';
import {
  ApartmentOutlined,
  BankOutlined,
  ClusterOutlined,
  FileTextOutlined,
  HomeOutlined,
  IdcardOutlined,
  LogoutOutlined,
  MehOutlined,
  MenuOutlined,
  MobileOutlined,
  MoonOutlined,
  SettingOutlined,
  ShopOutlined,
  SnippetsOutlined,
  SunOutlined,
  TeamOutlined,
  UserOutlined,
  UserSwitchOutlined,
} from '@ant-design/icons';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import AccountRequests from './accountRequests';
import HomePage from './homePage';
import Onsite from './onsite';
import Requests from './requests';
import PendingApproval from './pendingApproval';
import PreRegistered from './preRegistered';
import Profile from './profile';
import Residents from './residents';
import ResidentAccessRecord from './residentAccessRecord';
import Settings from './settings';
import ManageSites from './manageSites';
import MobileVersionGate from './mobileVersionGate';
import SiteConfig from './siteConfig';
import VehicleAccessRecord from './vehicleAccessRecord';
import VisitorAccessRecord from './visitorAccessRecord';
import atact40 from '../assets/atact40.png';
import {
  badgeOffsetTop,
  siderWidth,
  requestStatus,
  versionNumber,
} from '../common/constants';
import debugLog from '../common/debugLog';
import useAuthClaims from '../common/useAuthClaims';
import {
  db,
  firGetSiteNameSlug,
  firGetSiteRequestCount,
  firGetUserAssignedSiteId,
  firGetUserData,
  firMarkSignInAccepted,
  primaryAuth,
} from '../firebase';
import './home.scss';

export default function Home() {
  // D24 rev 77 (2026-05-15): used to gate the Manage Sites nav item +
  // route to users with the `root` custom claim (ATACT staff).
  const claims = useAuthClaims();
  const [badgeTop, setBadgeTop] = useState(0);
  const [collapsed, setCollapsed] = useState(false);
  const [currentUser, setCurrentUser] = useState(undefined);
  // D24 rev 74 (2026-05-15): read `accounts/{uid}.fullname` for the nav
  // display name. Previously we read `primaryAuth.currentUser.displayName`,
  // which is null for accounts created manually (Firebase Console / dev
  // shortcuts) — those accounts showed "--" in the sidebar even when
  // Firestore had a perfectly good `fullname`.
  const [userFullname, setUserFullname] = useState('');
  // const [isLocalSignIn, setIsLocalSignIn] = useState(true); // default for now
  const [menuTheme, setMenuTheme] = useState('dark');
  const [requestCount, setRequestCount] = useState({
    accountRequests: 0,
    pendingApproval: 0,
    preRegister: 0,
  });
  const [site, setSite] = useState({ id: '', name: '', slug: '' });
  // rev 106 (2026-05-18) — "Open as Admin" impersonation banner state.
  // Set when the root user has chosen to view as another site's admin.
  // The actual override is read from localStorage in the site-resolution
  // path below; this state mirrors it so the banner can rerender.
  const [impersonatingSite, setImpersonatingSite] = useState(null);

  const [modal, contextHolder] = Modal.useModal();

  const location = useLocation();
  const navigate = useNavigate();

  // destructure imports
  const { Content, Header, Sider } = Layout;
  const { appVersion } = versionNumber;

  const showModal = () => {
    modal.confirm({
      content: 'Are you sure you want to sign out?',
      icon: <MehOutlined />,
      onCancel: () => debugLog('-- User cancelled the modal'),
      onOk: () => {
        signOut(primaryAuth).catch((error) =>
          console.error('-- Error signing out:', error),
        );
      },
      title: 'Confirm Sign Out',
    });
  };

  const handleCollapsed = (value) => {
    setBadgeTop(value ? badgeOffsetTop : 0);
    setCollapsed(value);
  };

  const handleMenuClick = (e) =>
    e.key === 'signOut' ? showModal() : navigate(e.key);

  /**
   * Gets the request count for the specified collection and updates the badge for the corresponding menu item
   *
   * The result can be stored with a different key if one is specified
   *
   * @param {string} col The collection within the database to count requests from
   * @param {string} siteId The query will only include requests from this site ID
   * @param {number} status The query will only include requests with this status
   * @param {object} optParams Optional parameters
   * @param {string} [optParams.altKey=undefined] (Optional) Alternative key to store the value with, will use the collection name if none is specified. Default is `undefined`
   */
  const updateRequestCount = useCallback(
    (col, siteId, status, { altKey = undefined } = {}) => {
      const key = altKey ?? col; // if no alternative is specified, use collection name

      firGetSiteRequestCount(col, siteId, status)
        .then((reqCount) => {
          // only add/update the count for the specified key
          setRequestCount((prev) => ({
            ...prev,
            [key]: reqCount,
          }));
        })
        .catch((error) => {
          console.error(error);
        });
    },
    [],
  );

  const items = [
    {
      danger: true,
      icon: <LogoutOutlined />,
      id: 'menu-signout',
      key: 'signOut',
      label: 'Sign Out',
    },
    {
      disabled: true,
      icon: <MenuOutlined />,
      id: 'menu-version',
      label: `appVersion: ${appVersion}`,
    },
    {
      icon: <TeamOutlined />,
      id: 'menu-profile',
      key: '/home/profile',
      // Prefer Firestore fullname (always set for D24+ accounts), fall back
      // to Auth.displayName for legacy / manually-created admin users.
      label: (
        userFullname ||
        primaryAuth.currentUser?.displayName ||
        '--'
      ).toUpperCase(),
    },
    {
      icon: <HomeOutlined />,
      key: '/home',
      label: 'Home Page',
    },
    {
      icon: <FileTextOutlined />,
      key: '/home/residentAccessRecord',
      label: 'Access Records (Resident)',
    },
    {
      icon: <FileTextOutlined />,
      key: '/home/vehicleAccessRecord',
      label: 'Access Records (Vehicle)',
    },
    {
      icon: <FileTextOutlined />,
      key: '/home/visitorAccessRecord',
      label: 'Access Records (Visitor)',
    },
    {
      icon: <ShopOutlined />,
      key: '/home/onsite',
      label: 'Onsite',
    },
    {
      icon: <BankOutlined />,
      key: '/home/preRegistered',
      label: 'Pre-registered',
    },
    {
      icon: (
        <Badge
          count={requestCount.pendingApproval}
          offset={[0, badgeTop]}
          size="small"
          title="pending approval"
        >
          <UserOutlined />
        </Badge>
      ),
      key: '/home/pendingApproval',
      label: 'Pending Approval',
    },
    {
      icon: (
        <Badge
          count={requestCount.accountRequests}
          offset={[0, badgeTop]}
          size="small"
          title="pending requests"
        >
          <IdcardOutlined />
        </Badge>
      ),
      key: '/home/accountRequests',
      label: 'Account Requests',
    },
    {
      icon: <SnippetsOutlined />,
      key: '/home/requests',
      label: 'General Requests',
    },
    {
      icon: <UserSwitchOutlined />,
      key: '/home/residents',
      label: 'Residents',
    },
    {
      icon: <ApartmentOutlined />,
      key: '/home/siteConfig',
      label: 'Site Config',
    },
    // D24 rev 77 (2026-05-15): staff-only "Manage Sites" item. Filtered
    // out below if the user doesn't have the `root` custom claim.
    // rev 95 (2026-05-17) adds the "Mobile Version Gate" sibling under
    // the same root gate.
    ...(claims.root
      ? [
          {
            icon: <ClusterOutlined />,
            key: '/home/manageSites',
            label: 'Manage Sites',
          },
          {
            icon: <MobileOutlined />,
            key: '/home/mobileVersionGate',
            label: 'Mobile Version Gate',
          },
        ]
      : []),
    {
      icon: <SettingOutlined />,
      key: '/home/settings',
      label: 'Settings',
    },
    {
      disabled: true,
      key: '',
      label: '',
    },
  ];

  // rev 87 (2026-05-17) — Tier 1 session-kick. Watch the signed-in
  // user's own accounts/{uid} doc. If their `status` flips to 2
  // (Deactivated) while a session is live, sign them out + redirect
  // to /signIn with an alert. Idempotent: only fires once because we
  // signOut + navigate; the listener unmounts when home.jsx unmounts.
  //
  // Pairs with v2removeAdmin's revokeRefreshTokens (Tier 2): the
  // server-side revoke prevents fresh ID-token issuance for any
  // OTHER tab / device the same user might have open; this client-
  // side listener handles the CURRENT tab without waiting for a
  // token refresh to fail.
  useEffect(() => {
    if (!currentUser) return undefined;
    const unsub = onSnapshot(
      doc(db, 'accounts', currentUser),
      (snap) => {
        if (!snap.exists()) return;
        const data = snap.data() || {};
        // status 2 = Deactivated (rev 86 enum alignment). Defensive:
        // also kick on assignedSite mismatch (admin moved to another
        // site behind their back) — currently no flow does that, but
        // the check costs nothing.
        const isDeactivated = data.status === 2;
        if (!isDeactivated) return;
        // Tell the user, then sign them out. signOut triggers the
        // existing onAuthStateChanged handler which will navigate to
        // /signIn via the route guards on each Route element.
        Modal.warning({
          title: 'Your access has been revoked',
          content:
            'Your account is no longer active on this site. ' +
            'Contact your SuperAdmin or A TACT support if this is unexpected.',
          okText: 'Sign out',
          onOk: async () => {
            try {
              await signOut(primaryAuth);
            } catch (e) {
              /* swallow */
            }
            window.location.href = '/signIn';
          },
        });
      },
      (err) => {
        console.warn('-- accounts watcher error:', err);
      },
    );
    return () => unsub();
  }, [currentUser]);

  useEffect(() => {
    // listen for changes in user authentication state
    onAuthStateChanged(primaryAuth, (user) => {
      if (user) {
        // Fetch the Firestore profile for the nav fullname. Independent
        // of the site-resolution chain so a missing accounts doc never
        // blocks layout. Best-effort: silently falls back to empty string
        // and the label code uses Auth.displayName as the next fallback.
        firGetUserData(user.uid)
          .then((data) => {
            setUserFullname(data?.fullname || '');
          })
          .catch((e) => {
            console.warn('-- Could not load user fullname:', e);
            setUserFullname('');
          });

        // rev 85: if this staff account is Pending (status=3) — i.e.
        // they just clicked the invite link, set password, and signed
        // in for the first time — transition to Active (status=1) and
        // stamp acceptedAt. Idempotent: only writes when status===3.
        firMarkSignInAccepted(user.uid).catch((e) => {
          console.warn('-- Could not mark sign-in accepted:', e);
        });

        // check for assigned site (very important)
        firGetUserAssignedSiteId(user.uid).then(async (actualSiteId) => {
          // rev 106: "Open as Admin" impersonation override. Root users
          // can stash a target siteId in localStorage via the Manage Sites
          // → "Open as Admin" button; on next load (or after this re-resolve)
          // the whole home.jsx context renders against that site instead of
          // the operator's actual assignedSite. Per-callable checks still
          // gate writes by claim, so this is a VIEW-scope debug aid only.
          let effectiveSiteId = actualSiteId;
          let impersonating = null;
          try {
            const idTok = await user.getIdTokenResult();
            const isRoot = idTok?.claims?.root === true;
            const impersonatedSiteId =
              localStorage.getItem('impersonatedSiteId') || '';
            if (
              isRoot &&
              impersonatedSiteId &&
              impersonatedSiteId !== actualSiteId
            ) {
              effectiveSiteId = impersonatedSiteId;
              impersonating = impersonatedSiteId;
            }
          } catch (e) {
            console.warn(
              '-- Could not resolve root claim for impersonation:',
              e,
            );
          }
          // fetch site display name and slug
          firGetSiteNameSlug(effectiveSiteId)
            .then((nameSlug) => {
              const { pendings } = requestStatus; // destructure imports
              setSite({
                id: effectiveSiteId,
                name: nameSlug.name,
                slug: nameSlug.slug,
              });
              setImpersonatingSite(
                impersonating
                  ? { id: effectiveSiteId, name: nameSlug.name }
                  : null,
              );
              setCurrentUser(user.uid);
              updateRequestCount('accountRequests', effectiveSiteId, pendings);
              updateRequestCount('preRegister', effectiveSiteId, pendings, {
                altKey: 'pendingApproval',
              });
            })
            .catch((e) => {
              // Defensive: if the impersonated site was deleted between
              // the localStorage write and now, fall back to the real one.
              console.warn(
                '-- firGetSiteNameSlug failed; clearing impersonation:',
                e,
              );
              localStorage.removeItem('impersonatedSiteId');
              firGetSiteNameSlug(actualSiteId).then((nameSlug) => {
                const { pendings } = requestStatus;
                setSite({
                  id: actualSiteId,
                  name: nameSlug.name,
                  slug: nameSlug.slug,
                });
                setImpersonatingSite(null);
                setCurrentUser(user.uid);
                updateRequestCount('accountRequests', actualSiteId, pendings);
                updateRequestCount('preRegister', actualSiteId, pendings, {
                  altKey: 'pendingApproval',
                });
              });
            });
        });
      } else {
        debugLog('-- User not found');
        setCurrentUser(null);
        setUserFullname('');
        setSite({});
      }
    });
  }, [updateRequestCount]);

  return (
    <Layout className="main-layout">
      <Sider
        collapsed={collapsed}
        collapsible
        onCollapse={(value) => handleCollapsed(value)}
        theme={menuTheme}
        width={siderWidth}
      >
        <Menu
          items={items}
          mode="vertical"
          onClick={handleMenuClick}
          selectedKeys={[location.pathname]}
          theme={menuTheme}
        />
      </Sider>
      {contextHolder}
      <Layout className="site-layout">
        <Header className="site-header">
          <h2 className="header-title">
            <img alt="A TACT Logo" className="atact-logo" src={atact40} />
            <span>A TACT Connect Management | {site.name}</span>
          </h2>
          <span className="header-control">
            <Switch
              checkedChildren={<MoonOutlined />}
              defaultChecked
              onChange={(value) => setMenuTheme(value ? 'dark' : 'light')}
              unCheckedChildren={<SunOutlined />}
            />
          </span>
        </Header>
        {/* rev 106 — "Open as Admin" impersonation banner. Shows only
            when claims.root has set localStorage.impersonatedSiteId via
            Manage Sites → "Open as Admin". Click to exit. */}
        {impersonatingSite && (
          <div
            style={{
              background: '#fffbe6',
              borderBottom: '1px solid #ffe58f',
              padding: '8px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              fontSize: 13,
            }}
          >
            <span style={{ color: '#874d00', fontWeight: 600 }}>
              ⚠ Impersonation mode — viewing as Admin of{' '}
              <strong>{impersonatingSite.name}</strong>
            </span>
            <span style={{ color: '#874d00' }}>
              Root claim still active. Per-callable checks still apply.
            </span>
            <span style={{ flex: 1 }} />
            <button
              type="button"
              onClick={() => {
                localStorage.removeItem('impersonatedSiteId');
                window.location.href = '/home';
              }}
              style={{
                background: '#fff',
                border: '1px solid #ffd666',
                color: '#874d00',
                borderRadius: 4,
                padding: '4px 12px',
                cursor: 'pointer',
                fontSize: 12,
              }}
            >
              Exit impersonation
            </button>
          </div>
        )}
        <Content className="flex-container">
          <Routes>
            <Route
              path="/"
              element={
                currentUser !== null ? (
                  <HomePage site={site} />
                ) : (
                  <Navigate to="/signIn" replace />
                )
              }
            />
            <Route
              path="/profile"
              element={
                currentUser !== null ? (
                  <Profile />
                ) : (
                  <Navigate to="/signIn" replace />
                )
              }
            />
            <Route
              path="/residentAccessRecord"
              element={
                currentUser !== null ? (
                  <ResidentAccessRecord site={site} />
                ) : (
                  <Navigate to="/signIn" replace />
                )
              }
            />
            <Route
              path="/vehicleAccessRecord"
              element={
                currentUser !== null ? (
                  <VehicleAccessRecord site={site} />
                ) : (
                  <Navigate to="/signIn" replace />
                )
              }
            />
            <Route
              path="/visitorAccessRecord"
              element={
                currentUser !== null ? (
                  <VisitorAccessRecord site={site} />
                ) : (
                  <Navigate to="/signIn" replace />
                )
              }
            />
            <Route
              path="/onsite"
              element={
                currentUser !== null ? (
                  <Onsite site={site} />
                ) : (
                  <Navigate to="/signIn" replace />
                )
              }
            />
            <Route
              path="/preRegistered"
              element={
                currentUser !== null ? (
                  <PreRegistered site={site} />
                ) : (
                  <Navigate to="/signIn" replace />
                )
              }
            />
            <Route
              path="/pendingApproval"
              element={
                currentUser !== null ? (
                  <PendingApproval
                    badgeCount={requestCount.pendingApproval}
                    site={site}
                    updateRequestCount={updateRequestCount}
                  />
                ) : (
                  <Navigate to="/signIn" replace />
                )
              }
            />
            <Route
              path="/accountRequests"
              element={
                currentUser !== null ? (
                  <AccountRequests
                    badgeCount={requestCount.accountRequests}
                    site={site}
                    updateRequestCount={updateRequestCount}
                  />
                ) : (
                  <Navigate to="/signIn" replace />
                )
              }
            />
            <Route
              path="/requests"
              element={
                currentUser !== null ? (
                  <Requests site={site} />
                ) : (
                  <Navigate to="/signIn" replace />
                )
              }
            />
            <Route
              path="/residents"
              element={
                currentUser !== null ? (
                  <Residents site={site} />
                ) : (
                  <Navigate to="/signIn" replace />
                )
              }
            />
            <Route
              path="/siteConfig"
              element={
                currentUser !== null ? (
                  <SiteConfig siteId={site.id} />
                ) : (
                  <Navigate to="/signIn" replace />
                )
              }
            />
            <Route
              path="/manageSites"
              element={
                currentUser !== null ? (
                  <ManageSites />
                ) : (
                  <Navigate to="/signIn" replace />
                )
              }
            />
            <Route
              path="/mobileVersionGate"
              element={
                currentUser !== null ? (
                  <MobileVersionGate />
                ) : (
                  <Navigate to="/signIn" replace />
                )
              }
            />
            <Route
              path="/settings"
              element={
                currentUser !== null ? (
                  <Settings site={site} />
                ) : (
                  <Navigate to="/signIn" replace />
                )
              }
            />
          </Routes>
        </Content>
      </Layout>
    </Layout>
  );
}
